import { useNavigate } from "react-router-dom";
import { Dropdown, Button, Typography } from "antd";
import { DownOutlined } from "@ant-design/icons";
import heroImage from "../assets/Homepage.jpeg";

const { Title, Text } = Typography;

export default function Landing() {
  const navigate = useNavigate();

  const loginMenu = {
    items: [
      { key: "student", label: "Student" },
      { key: "teacher", label: "Teacher" },
    ],
    onClick: ({ key }) => navigate(`/login/${key}`),
  };

  const signupMenu = {
    items: [
      { key: "student", label: "Student" },
      { key: "teacher", label: "Teacher" },
    ],
    onClick: ({ key }) => navigate(`/signup/${key}`),
  };

  const features = [
    {
      title: "Courses & Scheduling",
      desc: "Browse courses, timings, and durations in one clean place.",
      icon: "📚",
    },
    {
      title: "Results Tracking",
      desc: "Students view their marks and grades anytime, anywhere.",
      icon: "📊",
    },
    {
      title: "Teacher Management",
      desc: "Admins manage teachers, students, and results efficiently.",
      icon: "🧑‍🏫",
    },
  ];

  return (
    <div className="min-h-screen bg-background font-body">
      {/* Navbar */}
      <header className="sticky top-0 z-50 backdrop-blur bg-background/80 border-b border-muted animate-fade-in">
        <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary text-white flex items-center justify-center font-extrabold text-lg shadow-soft">
              L
            </div>
            <span className="text-xl font-extrabold text-foreground font-heading">
              LearnHub <span className="text-primary">LMS</span>
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Dropdown menu={loginMenu} trigger={["click"]}>
              <Button className="font-semibold border-primary/30 text-foreground hover:text-primary">
                Login <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown menu={signupMenu} trigger={["click"]}>
              <Button className="bg-primary hover:opacity-90 border-none text-white font-semibold shadow-soft">
                Sign Up <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20 animate-hero-zoom"
        />
        <div className="relative max-w-7xl mx-auto px-6 py-28 md:py-36 text-center">
          <div className="inline-block mb-5 px-4 py-1.5 rounded-full bg-white/15 text-white text-sm font-medium backdrop-blur animate-fade-up">
            ✨ A smarter way to learn &amp; teach
          </div>
          <Title className="!text-white !text-4xl md:!text-6xl !leading-tight !font-extrabold animate-fade-up anim-delay-100">
            Empowering Learning for Students,
            <br className="hidden md:block" /> Teachers &amp; Admins
          </Title>
          <Text className="!text-white/90 !text-lg md:!text-xl max-w-2xl mx-auto block mt-5 animate-fade-up anim-delay-200">
            LearnHub is a modern Learning Management System to manage courses, track
            results, and connect every role in one beautiful platform.
          </Text>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up anim-delay-300">
            <Button
              size="large"
              className="bg-white text-primary hover:opacity-90 border-none font-bold px-8 h-12 shadow-soft"
              onClick={() => navigate("/signup/student")}
            >
              Get Started
            </Button>
            <Button
              size="large"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-12"
              onClick={() => navigate("/login/student")}
            >
              Student Login
            </Button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto py-20 px-6">
        <div className="text-center mb-12 animate-fade-up">
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground font-heading">
            Why <span className="text-gradient">LearnHub?</span>
          </h2>
          <p className="text-muted-foreground mt-3">
            Everything your institution needs, thoughtfully designed.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`bg-card rounded-2xl shadow-card p-8 border border-muted hover:-translate-y-2 hover:shadow-soft transition-all duration-300 animate-fade-up anim-delay-${(i + 1) * 100}`}
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 text-2xl flex items-center justify-center mb-5">
                {f.icon}
              </div>
              <h3 className="text-xl font-bold text-foreground font-heading mb-2">
                {f.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto rounded-3xl bg-gradient-primary text-white p-10 md:p-14 text-center shadow-soft animate-fade-up">
          <h2 className="text-3xl md:text-4xl font-extrabold font-heading">
            Ready to transform your classroom?
          </h2>
          <p className="text-white/85 mt-3 max-w-xl mx-auto">
            Create your account in minutes and join thousands of learners on LearnHub.
          </p>
          <Button
            size="large"
            className="mt-7 bg-white text-primary hover:opacity-90 border-none font-bold px-8 h-12"
            onClick={() => navigate("/signup/student")}
          >
            Create Free Account
          </Button>
        </div>
      </section>

      <footer className="text-center text-muted-foreground text-sm py-8 border-t border-muted">
        © {new Date().getFullYear()} LearnHub LMS. All rights reserved.
      </footer>
    </div>
  );
}
